import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { Facebook, Twitter, Instagram, Youtube, Linkedin, Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import axios from 'axios';
import useSocialFlags from '../hooks/useSocialFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const { socialEnabled } = useSocialFlags();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/site-settings`);
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }
    try {
      setNewsletterLoading(true);
      const res = await axios.post(`${API_URL}/api/newsletter/subscribe`, { email: newsletterEmail });
      toast.success(res.data.message);
      setNewsletterEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setNewsletterLoading(false);
    }
  };

  const identity = settings?.identity || {
    site_name: 'InfoFlow',
    slogan: "Comprendre le monde, autrement.",
    email: 'contact@infoflow.studio',
    phone: '+33 7 59 00 03 15',
    city: 'Paris, France'
  };

  const social = settings?.social || {};

  const whatsappNumber = social.whatsapp || settings?.identity?.phone?.replace(/\s+/g, '') || '';
  const whatsappUrl = whatsappNumber ? `https://wa.me/${whatsappNumber.replace(/^\+/, '')}?text=Bonjour%20InfoFlow%20!` : '';

  const socialLinks = [
    { icon: Facebook, label: 'Facebook', url: social.facebook },
    { icon: Twitter, label: 'Twitter', url: social.twitter },
    { icon: Instagram, label: 'Instagram', url: social.instagram },
    { icon: Youtube, label: 'Youtube', url: social.youtube },
    { icon: Linkedin, label: 'LinkedIn', url: social.linkedin },
    { icon: MessageCircle, label: 'WhatsApp', url: whatsappUrl },
  ];

  const footerLinks = settings?.footer_links;

  const sections = [
    {
      title: 'Rubriques',
      links: [
        { name: 'Actualité', href: '/category/actualite' },
        { name: 'Politique', href: '/category/politique' },
        { name: 'Économie', href: '/category/economie' },
        { name: 'International', href: '/category/international' },
        { name: 'Culture', href: '/category/culture' },
        { name: 'Sport', href: '/category/sport' },
      ]
    },
    {
      title: 'Services',
      links: footerLinks?.services || [
        { name: 'Newsletters', href: '#' },
        { name: 'Podcasts', href: '/podcasts' },
        { name: 'Vidéos', href: '#' },
        { name: 'Archives', href: '#' },
        { name: 'Flux RSS', href: '/podcasts' },
      ]
    },
    {
      title: 'À propos',
      links: footerLinks?.about || [
        { name: 'Qui sommes-nous', href: '/a-propos' },
        { name: 'Rédaction', href: '#' },
        { name: 'Contact', href: '#' },
        { name: 'Publicité', href: '#' },
        { name: 'Recrutement', href: '#' },
      ]
    },
    {
      title: 'Légal',
      links: footerLinks?.legal || [
        { name: 'Mentions légales', href: '/mentions-legales' },
        { name: 'CGU', href: '/cgu' },
        { name: 'Confidentialité', href: '/confidentialite' },
        { name: 'Cookies', href: '/cookies' },
        { name: 'Charte IA', href: '/charte-ia' },
      ]
    },
  ];

  // Split site name for styled rendering (e.g., "InfoFlow" -> "Info" + "Flow")
  const siteName = identity.site_name || 'InfoFlow';
  const nameHalf = Math.ceil(siteName.length / 2);
  const namePart1 = siteName.slice(0, nameHalf);
  const namePart2 = siteName.slice(nameHalf);

  return (
    <footer className="bg-foreground text-background mt-8 sm:mt-16 pb-24 sm:pb-20">
      <div className="container mx-auto px-4 py-12">
        {/* Top Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <span className="text-3xl font-serif font-bold">
                {namePart1}<span className="text-[#D62828]">{namePart2}</span>
              </span>
            </Link>
            <p className="text-muted-foreground text-sm mb-4">
              {identity.slogan}
            </p>
            {socialEnabled && (
              <div className="flex gap-3 items-center">
                {socialLinks.map(({ icon: Icon, label, url }) => (
                  url ? (
                    <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                      className={`transition-colors ${label === 'WhatsApp' ? 'text-[#25D366] hover:text-[#20BD5A]' : 'text-muted-foreground hover:text-background'}`}
                      aria-label={label} title={label === 'WhatsApp' ? 'Rejoignez notre groupe WhatsApp' : label}>
                      <Icon className="h-5 w-5" />
                    </a>
                  ) : (
                    <button key={label} onClick={() => toast.info(`Page ${label} bientôt disponible`)}
                      className="text-muted-foreground hover:text-background transition-colors" aria-label={label}>
                      <Icon className="h-5 w-5" />
                    </button>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Links */}
          {sections.map((section) => (
            <div key={section.title}>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.href === '#' ? (
                      <button
                        onClick={() => toast.info('Cette page sera bientôt disponible')}
                        className="text-muted-foreground hover:text-background text-sm transition-colors text-left"
                      >
                        {link.name}
                      </button>
                    ) : link.href?.startsWith('http') ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-background text-sm transition-colors"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-muted-foreground hover:text-background text-sm transition-colors"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="bg-muted-foreground/10 rounded-lg p-6 mb-12">
          <div className="max-w-xl mx-auto text-center">
            <h4 className="font-semibold text-lg mb-1">Newsletter</h4>
            <p className="text-muted-foreground text-sm mb-4">
              Recevez les dernières actualités directement dans votre boîte mail
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Votre adresse email"
                className="flex-1 px-4 py-2 rounded-md bg-background text-foreground border border-muted-foreground/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#C0392B]"
                required
              />
              <button
                type="submit"
                disabled={newsletterLoading}
                className="px-5 py-2 bg-[#C0392B] hover:bg-[#A93226] text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {newsletterLoading ? '...' : "S'inscrire"}
              </button>
            </form>
          </div>
        </div>

        <Separator className="bg-muted-foreground/20 mb-8" />

        {/* Contact Info */}
        <div className="flex flex-wrap gap-3 sm:gap-6 mb-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span>{identity.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            <span>{identity.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>{identity.city}</span>
          </div>
        </div>

        {/* Legal links */}
        <div className="flex flex-wrap justify-center gap-x-1 gap-y-1 mb-6 text-xs text-muted-foreground">
          <Link to="/mentions-legales" className="hover:text-background transition-colors">Mentions légales</Link>
          <span>|</span>
          <Link to="/cgu" className="hover:text-background transition-colors">CGU</Link>
          <span>|</span>
          <Link to="/confidentialite" className="hover:text-background transition-colors">Confidentialité</Link>
          <span>|</span>
          <Link to="/cookies" className="hover:text-background transition-colors">Cookies</Link>
          <span>|</span>
          <Link to="/charte-ia" className="hover:text-background transition-colors">Charte IA</Link>
        </div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-2 md:gap-4 text-xs sm:text-sm text-muted-foreground">
          <p>&copy; {currentYear} {siteName}. Tous droits réservés.</p>
          <p className="flex items-center gap-1">
            Propulsé par l'IA avec
            <span className="text-[#D62828]">&hearts;</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
